import { Component, OnInit, ViewChild, Input, OnChanges, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';
import { Location } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { NgForm, UntypedFormBuilder, UntypedFormGroup, Validators, AbstractControl, FormControl } from '@angular/forms';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';

import { Subject, Observable, of } from 'rxjs';
import { map, merge, take } from 'rxjs/operators';
import { AlertService, MessageSeverity } from '../../../services/alert.service';
import { AppTranslationService } from '../../../services/app-translation.service';
import { DateFormatterPipe } from '../../../pipes/dateformatter.pipe';
import { fadeInOut } from '../../../services/animations';
import { ReportMetadataService, MethodVersionViewModel, Int32StringTuple, MethodVersionService } from '../../index';
import { AuthService } from '../../../services/auth.service';
import { Permission } from '../../../models/permission.model';
import { CanComponentDeactivate } from '../../../services/deactivate-guard';
import { Utilities } from '../../../services/utilities';

@Component({
    selector: 'app-method-version-details',
    templateUrl: './method-version-details.component.html',
    styleUrls: ['./method-version-details.component.scss'],
    animations: [fadeInOut],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MethodVersionDetailsComponent implements OnInit, OnChanges, CanComponentDeactivate {

    @ViewChild('form', { static: true }) form: NgForm;
    @ViewChild('app-report-list', { static: true })

    methodVersion: MethodVersionViewModel;
    businessCategories$: Observable<Int32StringTuple[]>;
    reportOutputTemplates$: Observable<Int32StringTuple[]>;
    createdByName$: Observable<string>;
    changedByName$: Observable<string>;

    id: number;
    methodVersionForm: UntypedFormGroup;
    private onRecordSaved = new Subject<MethodVersionViewModel>();
    reportSaved$ = this.onRecordSaved.asObservable();
    hasPermissionToChange$: Observable<boolean> = of(false);

    public isNew = false;
    private isSaving = false;

    constructor(
        private alertService: AlertService,
        private translationService: AppTranslationService,
        private methodVersionService: MethodVersionService,
        private reportMetadataService: ReportMetadataService,
        private formBuilder: UntypedFormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private location: Location,
        private snackBar: MatSnackBar,
        private authService: AuthService
    ) {
        this.id = Number.parseInt(this.route.snapshot.paramMap.get('id'));
    }

    ngOnInit(): void {
        this.buildForm();

        this.businessCategories$ = this.reportMetadataService.getBusinessCategoryMetadata();
        this.hasPermissionToChange$ = of(this.authService.userHasPermission(Permission.changeReports));
        if (this.id) {
            this.methodVersionService.getMethodVersionById(this.id).subscribe(ok => this.dataLoaded(ok), error => this.alertService.showStickyMessage(error));
            this.reportOutputTemplates$ = this.reportMetadataService.getReportOutputTemplates(false);
        } else {
            this.reportOutputTemplates$ = this.reportMetadataService.getReportOutputTemplates(true);
            this.isNew = true;
            this.methodVersion = this.initializeEmptyObject();
            this.resetForm();
        }
    }

    ngOnChanges(): void {
        if (this.methodVersion) {
            this.isNew = false;
        }
        else {
            this.isNew = true;
            this.methodVersion = this.initializeEmptyObject();
        }

        this.resetForm();
    }


    dataLoaded(methodVersion: MethodVersionViewModel) {
        this.methodVersion = methodVersion;

        this.isNew = false;
        this.resetForm();
    }

    public getFormControl(name: string): AbstractControl {
        return this.methodVersionForm.controls[name];
    }


    private buildForm() {
        this.methodVersionForm = this.formBuilder.group({
            methodVersionId: '',
            name: ['', Validators.compose([Validators.required, Validators.maxLength(100)])],
            enabled: [{ value: '', disabled: true }],
            businessCategoryId: ['', Validators.required],
            createdBy: '',
            createdDate: '',
            updatedBy: '',
            updatedDate: '',
            preview: '',
            includeRequirements: '',
            reportOutputTemplateId: ['', Validators.required],
        });
    }

    public resetForm() {
        if (!this.methodVersion) {
            this.isNew = true;
            this.methodVersion = this.initializeEmptyObject();
        }

        this.methodVersionForm.reset({
            methodVersionId: this.methodVersion.methodVersionId || '',
            name: this.methodVersion.name || '',
            enabled: this.methodVersion.enabled || '',
            businessCategoryId: this.methodVersion.businessCategoryId || '',
            createdBy: this.methodVersion.createdBy || '',
            createdDate: new DateFormatterPipe().transform(this.methodVersion.createdDate, true) || '',
            updatedBy: this.methodVersion.updatedBy || '',
            updatedDate: new DateFormatterPipe().transform(this.methodVersion.updatedDate, true) || '',
            preview: this.methodVersion.preview || '',
            includeRequirements: this.methodVersion.includeRequirements || '',
            reportOutputTemplateId: this.methodVersion.reportOutputTemplateId || ''
        });

        if (this.methodVersion.createdBy) {
            this.createdByName$ = this.reportMetadataService.getUserById(this.methodVersion.createdBy).pipe(map(t => t.item2));
        }
        if (this.methodVersion.updatedBy) {
            this.changedByName$ = this.reportMetadataService.getUserById(this.methodVersion.updatedBy).pipe(map(t => t.item2));
        }
    }

    private initializeEmptyObject(): MethodVersionViewModel {
        var result = <MethodVersionViewModel>{};
        result.enabled = false;
        result.preview = true;
        result.includeRequirements = false;

        return result;
    }

    private prepareSave(): MethodVersionViewModel {
        const formModel = this.methodVersionForm.value;

        return {
            methodVersionId: this.isNew ? null : formModel.methodVersionId,
            name: formModel.name,
            enabled: formModel.enabled ? true : false,
            businessCategoryId: formModel.businessCategoryId,
            preview: formModel.preview ? true : false,
            includeRequirements: formModel.includeRequirements ? true : false,
            reportOutputTemplateId: formModel.reportOutputTemplateId
        };
    }

    public save() {
        if (!this.methodVersionForm.valid) {
            this.alertService.showValidationError();
            return;
        }

        this.isSaving = true;
        this.alertService.startLoadingMessage(this.translationService.getTranslation("globalTexts.SavingMessage"));

        const methodVersion = this.prepareSave();

        if (this.isNew) {
            this.methodVersionService.createMethodVersion(methodVersion).subscribe(
                t => this.saveCompleted(t),
                error => this.saveFailed(error));
        }
        else {
            this.methodVersionService.updateMethodVersion(methodVersion.methodVersionId, methodVersion).subscribe(
                t => this.saveCompleted(t),
                error => this.saveFailed(error));
        }
    }

    public cancel() {
        this.location.back();
    }

    activate(): void {
        let message: string = this.translationService.getTranslation("commands.Activate") +
            " " +
            this.methodVersion.name +
            "?";
        let action: string = this.translationService.getTranslation("commands.Activate").toUpperCase();

        this.snackBar.open(message, action, { duration: 5000 })
            .onAction().subscribe(() => {
                this.alertService.startLoadingMessage("Aktiverar metodversion");

                this.methodVersionService.approveMethodVersion(this.methodVersion.methodVersionId)
                    .subscribe(results => {
                            this.alertService.stopLoadingMessage();
                            this.alertService.showStickyMessage(
                                this.translationService.getTranslation("commands.Activate"),
                                this.methodVersion.name + " " + this.translationService.getTranslation("globalTexts.activated"),
                                MessageSeverity.success);

                            setTimeout(() => this.location.back(), 1000);
                        },
                        error => {
                            this.alertService.stopLoadingMessage();

                            let errorMessage = this.translationService.getTranslation("globalTexts.Error") +
                                ": " +
                                Utilities.getHttpResponseMessage(error);

                            this.alertService.showStickyMessage(
                                this.translationService.getTranslation("globalTexts.Error"),
                                errorMessage,
                                MessageSeverity.error,
                                error);
                        });
            });
    }

    createTestReport(): void {
        this.router.navigate(['reports/report'],
            { queryParams: { methodVersionId: this.id, businesscategoryid: this.methodVersion.businessCategoryId } });
    }

    rebuild(): void {
        this.isSaving = true;
        this.alertService.startLoadingMessage("Bygger struktur för metodversion...");

        this.methodVersionService.buildMethodVersion(this.id).subscribe(t => this.actionCompleted(t ? "Strukturen byggd" : "Strukturen kunde inte byggas"), error => this.saveFailed(error));
    }

    refresh(): void {
        this.isSaving = true;
        this.alertService.startLoadingMessage("Uppdaterar texter för metodversion...");

        this.methodVersionService.refreshMethodVersion(this.id).subscribe(t => this.actionCompleted(t ? "Texter uppdaterade" : "Texterna kunde inte uppdateras"), error => this.saveFailed(error));
    }

    delete(): void {
        let message: string = this.translationService.getTranslation("commands.Delete") +
            " " +
            this.methodVersion.name +
            "?";
        let action: string = this.translationService.getTranslation("commands.Delete").toUpperCase();

        this.snackBar.open(message, action, { duration: 5000 })
            .onAction().subscribe(() => {
                this.alertService.startLoadingMessage(
                    this.translationService.getTranslation("globalTexts.DeletingMessage"));

                this.methodVersionService.deleteMethodVersion(this.methodVersion.methodVersionId)
                    .subscribe(results => {
                            this.alertService.stopLoadingMessage();
                            this.alertService.showStickyMessage(
                                this.translationService.getTranslation("commands.Delete"),
                                this.methodVersion.name + " " + this.translationService.getTranslation("globalTexts.deleted"),
                                MessageSeverity.success);

                            setTimeout(() => this.location.back(), 1000);
                        },
                        error => {
                            this.alertService.stopLoadingMessage();

                            let errorMessage = this.translationService.getTranslation("globalTexts.Error") +
                                ": " +
                                Utilities.getHttpResponseMessage(error);

                            this.alertService.showStickyMessage(
                                this.translationService.getTranslation("globalTexts.DeleteError"),
                                errorMessage,
                                MessageSeverity.error,
                                error);
                        });
            });
    }

    // Prevent leaving the form accidentaly if the form is dirty
    canDeactivate(): Observable<boolean> {
        this.alertService.resetStickyMessage();

        if (this.methodVersionForm.dirty) {
            let snackbar = this.snackBar.open(this.translationService.getTranslation("globalTexts.DirtyFormBackQuery"),
                this.translationService.getTranslation("commands.OK"),
                { duration: 5000 });

            let close = snackbar.onAction().pipe(map(() => { return true; }));
            let remain = snackbar.afterDismissed().pipe(map(() => { return false; }));

            return close.pipe(
                merge(remain),
                take(1));

        } else {
            return of(true);
        }
    }

    private saveCompleted(methodVersion: MethodVersionViewModel) {
        this.methodVersion = methodVersion;
        this.id = methodVersion.methodVersionId;
        var doRebuild: boolean = this.isNew;
        this.isNew = false;

        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showMessage(this.translationService.getTranslation("commands.Save"), methodVersion.name + " " + this.translationService.getTranslation("globalTexts.saved"), MessageSeverity.success);
        this.resetForm();

        this.onRecordSaved.next(this.methodVersion);

        if (doRebuild) {
            // Initialize a rebuild
            this.rebuild();
        }
    }

    private actionCompleted(message: string) {
        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showMessage(this.translationService.getTranslation("commands.Save"), message, MessageSeverity.success);
    }

    private saveFailed(error: any) {
        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showStickyMessage(this.translationService.getTranslation("globalTexts.CouldNotSave"), this.translationService.getTranslation("globalTexts.SaveErrorMessage"), MessageSeverity.error, error);
        this.alertService.showStickyMessage(error, null, MessageSeverity.error);
    }

    hasPermissionToDelete(): boolean {
        return this.authService.userHasPermission(Permission.changeBusinesses);
    }

    methodVersionIsActive(): boolean {
        return this.getFormControl("enabled").value === true;
    }

}
